import { darken, lighten } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { subsidy_type_enum } from '@predium/client-graphql';
import {
  class_of_use_enum,
  climate_risk_type_enum,
  consumption_type_enum,
  efficiency_class_enum,
  energy_source_type_enum,
  eu_taxonomy_compliance_enum,
  tax_bracket_enum,
} from '@predium/enums';
import { ActionPlanImpactTrendsChartsColors } from '@predium/utils';

function createGradient(color1: string, color2: string) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

interface GradientsPaletteOptions {
  primary: string;
  info: string;
  success: string;
  warning: string;
  error: string;
}

interface ChartPaletteOptions {
  violet: string[];
  blue: string[];
  green: string[];
  yellow: string[];
  red: string[];
}

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
  interface Palette {
    gradients: GradientsPaletteOptions;
    chart: ChartPaletteOptions;
    energyEfficiencyClassColors: { [key in efficiency_class_enum]: PaletteColor };
    co2EfficiencyClassColorsFR: { [key in efficiency_class_enum]: PaletteColor };
    energyEfficiencyClassColorsFR: { [key in efficiency_class_enum]: PaletteColor };
    energyEfficiencyClassColorsNL: { [key in efficiency_class_enum]: PaletteColor };
    climateRiskColors: { [key in climate_risk_type_enum]: PaletteColor };
    classOfUseColors: { [key in class_of_use_enum]: PaletteColor };
    taxBracketColors: { [key in tax_bracket_enum]: PaletteColor };
    energySourceTypeColors: { [key in energy_source_type_enum]: PaletteColor };
    euTaxonomyColors: { [key in eu_taxonomy_compliance_enum]: PaletteColor };
    subsidyTypeColors: { [key in subsidy_type_enum]: PaletteColor };
    consumptionColors: { [key in consumption_type_enum]: PaletteColor };
    areaColors: {
      areas: {
        bgColor: string;
        textColor: string;
      }[];
    };
    link: string;
    excessEmissionCostColors: {
      cost: string;
      value: string;
    };
    scenarioImpactGraphColors: {
      primaryEnergy: string;
      yearlyCost: string;
      co2Emissions: string;
      finalEnergy: string;
      targetPath: string;
      statusQuo: string;
    };
    actionPlanImpactTrendsChartsColors: ActionPlanImpactTrendsChartsColors;
    scenarioComparisonColors: {
      scenarioOne: string;
      scenarioTwo: string;
      statusQuo: string;
    };
    customTargetColors: { marker: string; line: string };
    costByRenovationCategoryColors: {
      envelope: string;
      technology: string;
      energy_source: string;
      custom: string;
    };
    deviationFromDemandColors: {
      gt_30: string;
      pos_10_30: string;
      neg_10_10: string;
      neg_30_neg_10: string;
      lt_neg_30: string;
    };
    heatingEnergySourcesDevelopment: {
      districtHeating: string;
      electricity: string;
      gas: string;
      oil: string;
      other: string;
    };
    investmentCost: string;
    targetPath: string;
    statusQuo: string;
    strandingRange: string;
    strandingPath: string;
  }
  interface PaletteOptions {
    gradients: GradientsPaletteOptions;
    chart: ChartPaletteOptions;
  }
}

declare module '@mui/material' {
  interface Color {
    0: string;
    500_8: string;
    500_12: string;
    500_16: string;
    500_24: string;
    500_32: string;
    500_48: string;
    500_56: string;
    500_80: string;
  }
}

// SETUP COLORS
const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8),
};

const GREEN_EXTRA = {
  100: '#C8FAD6',
  300: '#5BE49B',
  500: '#00A76F',
  700: '#007867',
  900: '#004B50',
};

const VIOLET = {
  100: '#EFD6FF',
  300: '#C684FF',
  500: '#8E33FF',
  700: '#5119B7',
  900: '#27097A',
};

const BLUE = {
  100: '#CAFDF5',
  300: '#61F3F3',
  500: '#00B8D9',
  700: '#006C9C',
  900: '#003768',
};

const GREEN = {
  100: '#D3FCD2',
  300: '#77ED8B',
  500: '#22C55E',
  700: '#118D57',
  900: '#065E49',
};

const YELLOW = {
  100: '#FFF5CC',
  300: '#FFD666',
  500: '#FFAB00',
  700: '#B76E00',
  900: '#7A4100',
};

const RED = {
  100: '#FFE9D5',
  300: '#FFAC82',
  500: '#FF5630',
  700: '#B71D18',
  900: '#7A0916',
};

const RED_ALT_FIRE = {
  100: '#F9B1AB',
  300: '#F68A81',
  500: '#F03C2D',
  700: '#A82A1F',
  900: '#781E16',
};

const RED_ALT_WINE = {
  100: '#DCA2AD',
  300: '#CA7485',
  500: '#A81834',
  700: '#751024',
  900: '#540C1A',
};

const BLUE_ALT_NAVY = {
  100: '#AFD7F8',
  300: '#7CB7E7',
  500: '#0C7FDD',
  700: '#18629E',
  900: '#0F4774',
};

const BLUE_ALT_OCEAN = {
  100: '#A7DBF1',
  300: '#7BC9EB',
  500: '#23A6DE',
  700: '#18749B',
  900: '#11536F',
};

const GREEN_ALT_GRASS = {
  100: '#CCE7BB',
  300: '#B2DB9A',
  500: '#80C457',
  700: '#59893C',
  900: '#40622B',
};

const GREEN_ALT_OLIVE = {
  100: '#E0EEB7',
  300: '#D1E593',
  500: '#B3D54C',
  700: '#7D9535',
  900: '#596A26',
};

const YELLOW_ALT_OCRE = {
  100: '#F3F1AF',
  300: '#EDEA87',
  500: '#E1DD37',
  700: '#9D9A26',
  900: '#706E1B',
};

const YELLOW_ALT_SUN = {
  100: '#FCF0A6',
  300: '#FAE97A',
  500: '#F8DB22',
  700: '#AD9917',
  900: '#7C6D11',
};

const ORANGE_ALT_ORANGE = {
  100: '#FCE3AB',
  300: '#FBD582',
  500: '#F9BA2F',
  700: '#AE8220',
  900: '#7C5D17',
};

const ORANGE_ALT_CARMINE = {
  100: '#FBCBAE',
  300: '#F9B185',
  500: '#F57E35',
  700: '#AB5825',
  900: '#7A3F1A',
};

const PRIMARY = {
  lighter: '#C8FACD',
  light: '#5BE584',
  main: '#000000',
  dark: '#555555',
  darker: '#005249',
};
const SECONDARY = {
  lighter: '#D6E4FF',
  light: '#84A9FF',
  main: '#3366FF',
  dark: '#1939B7',
  darker: '#091A7A',
};
const INFO = {
  lighter: '#00B8D914',
  light: BLUE[300],
  main: BLUE[500],
  dark: BLUE[700],
  darker: BLUE[900],
};
const SUCCESS = {
  lighter: GREEN[100],
  light: GREEN[300],
  main: GREEN[500],
  dark: GREEN[700],
  darker: GREEN[900],
};
const WARNING = {
  lighter: YELLOW[100],
  light: YELLOW[300],
  main: YELLOW[500],
  dark: YELLOW[700],
  darker: YELLOW[900],
};
const ERROR = {
  lighter: RED[100],
  light: RED[300],
  main: RED[500],
  dark: RED[700],
  darker: RED[900],
};

type ShadedColor = {
  lighter: string;
  light: string;
  main: string;
  dark: string;
  darker: string;
};

const lighterCoefficient = 0.6;
const lightCoefficient = 0.4;
const darkCoefficient = 0.3;
const darkerCoefficient = 0.5;

const createShadedColor = (color: string, overrides?: Partial<ShadedColor>): ShadedColor => ({
  lighter: lighten(color, lighterCoefficient),
  light: lighten(color, lightCoefficient),
  main: color,
  dark: darken(color, darkCoefficient),
  darker: darken(color, darkerCoefficient),
  ...overrides,
});

// Slots with no standard A–G rating — grey for all palettes; H gets a real colour only in the DE scale
const greyFallbacks = {
  [efficiency_class_enum.AAAAAA]: createShadedColor(GREY[400]),
  [efficiency_class_enum.AAAAA]: createShadedColor(GREY[400]),
  [efficiency_class_enum.AAAA]: createShadedColor(GREY[400]),
  [efficiency_class_enum.AAA]: createShadedColor(GREY[400]),
  [efficiency_class_enum.AA]: createShadedColor(GREY[400]),
  [efficiency_class_enum.UNKNOWN]: createShadedColor(GREY[400]),
  [efficiency_class_enum.NOT_APPLICABLE]: createShadedColor(GREY[400]),
};

const energyEfficiencyClassColors: Record<efficiency_class_enum, ShadedColor> = {
  ...greyFallbacks,
  [efficiency_class_enum.H]: createShadedColor(RED_ALT_WINE[500]),
  // The NL-only A+++–A+++++ bands share AAA's navy — like the official Dutch scale, which paints
  // every A-variant the same colour.
  [efficiency_class_enum.AAAAAA]: createShadedColor(BLUE_ALT_NAVY[500]),
  [efficiency_class_enum.AAAAA]: createShadedColor(BLUE_ALT_NAVY[500]),
  [efficiency_class_enum.AAAA]: createShadedColor(BLUE_ALT_NAVY[500]),
  [efficiency_class_enum.AAA]: createShadedColor(BLUE_ALT_NAVY[500]),
  [efficiency_class_enum.AA]: createShadedColor(BLUE_ALT_OCEAN[500]),
  [efficiency_class_enum.A]: createShadedColor(GREEN_ALT_GRASS[500]),
  [efficiency_class_enum.B]: createShadedColor(GREEN_ALT_OLIVE[500]),
  [efficiency_class_enum.C]: createShadedColor(YELLOW_ALT_OCRE[500]),
  [efficiency_class_enum.D]: createShadedColor(YELLOW_ALT_SUN[500]),
  [efficiency_class_enum.E]: createShadedColor(ORANGE_ALT_ORANGE[500]),
  [efficiency_class_enum.F]: createShadedColor(ORANGE_ALT_CARMINE[500]),
  [efficiency_class_enum.G]: createShadedColor(RED_ALT_FIRE[500]),
};

const greyShade = createShadedColor(GREY[400]);

// French DPE primary energy efficiency class scale (official hex values, fixed by arrêté DPE)
const energyEfficiencyClassColorsFR: Record<efficiency_class_enum, ShadedColor> = {
  ...greyFallbacks,
  [efficiency_class_enum.H]: greyShade,
  [efficiency_class_enum.A]: createShadedColor('#009036'),
  [efficiency_class_enum.B]: createShadedColor('#52AE32'),
  [efficiency_class_enum.C]: createShadedColor('#C8D400'),
  [efficiency_class_enum.D]: createShadedColor('#FCEA26'),
  [efficiency_class_enum.E]: createShadedColor('#F8B334'),
  [efficiency_class_enum.F]: createShadedColor('#EA690B'),
  [efficiency_class_enum.G]: createShadedColor('#E30C1C'),
};

// Official Dutch energy-label scale (Rijksoverheid palette) — every A-variant shares the same
// green, so only the printed label distinguishes A from A+++++.
const energyEfficiencyClassColorsNL: Record<efficiency_class_enum, ShadedColor> = {
  ...greyFallbacks,
  [efficiency_class_enum.H]: greyShade,
  [efficiency_class_enum.AAAAAA]: createShadedColor('#009037'),
  [efficiency_class_enum.AAAAA]: createShadedColor('#009037'),
  [efficiency_class_enum.AAAA]: createShadedColor('#009037'),
  [efficiency_class_enum.AAA]: createShadedColor('#009037'),
  [efficiency_class_enum.AA]: createShadedColor('#009037'),
  [efficiency_class_enum.A]: createShadedColor('#009037'),
  [efficiency_class_enum.B]: createShadedColor('#55AB26'),
  [efficiency_class_enum.C]: createShadedColor('#C8D100'),
  [efficiency_class_enum.D]: createShadedColor('#FFEC00'),
  [efficiency_class_enum.E]: createShadedColor('#FABA00'),
  [efficiency_class_enum.F]: createShadedColor('#EB6909'),
  [efficiency_class_enum.G]: createShadedColor('#E2001A'),
};

// French DPE GES (CO2) indicator — official CMYK shades per arrêté DPE
// A: C5 M10 Y0 K10 | B: C10 M35 Y0 K10 | C: C15 M50 Y0 K15
// D: C20 M65 Y0 K15 | E: C20 M80 Y0 K15 | F: C25 M95 Y0 K20 | G: C35 M100 Y0 K20
const co2EfficiencyClassColorsFR: Record<efficiency_class_enum, ShadedColor> = {
  ...greyFallbacks,
  [efficiency_class_enum.H]: greyShade,
  [efficiency_class_enum.A]: createShadedColor('#DACFE6'),
  [efficiency_class_enum.B]: createShadedColor('#CF95E6'),
  [efficiency_class_enum.C]: createShadedColor('#B86CD9'),
  [efficiency_class_enum.D]: createShadedColor('#AD4CD9'),
  [efficiency_class_enum.E]: createShadedColor('#AD2BD9'),
  [efficiency_class_enum.F]: createShadedColor('#990ACC'),
  [efficiency_class_enum.G]: createShadedColor('#8500CC'),
};

const climateRiskColors: Record<climate_risk_type_enum, ShadedColor> = {
  [climate_risk_type_enum.EARTHQUAKE]: createShadedColor('#DEA876'),
  [climate_risk_type_enum.FOREST_FIRE]: createShadedColor('#E86A59'),
  [climate_risk_type_enum.HAIL]: createShadedColor('#739BCB'),
  [climate_risk_type_enum.HEAT]: createShadedColor('#D90000'),
  [climate_risk_type_enum.HEAVY_RAIN]: createShadedColor('#659FCA'),
  [climate_risk_type_enum.LIGHTNING_STRIKE]: createShadedColor('#F4F07C'),
  [climate_risk_type_enum.SNOW_LOAD]: createShadedColor('#3B6595'),
  [climate_risk_type_enum.WINTER_STORM]: createShadedColor('#78B0F2'),
};

const classOfUseColors: Record<class_of_use_enum, { main: string; light: string }> = {
  [class_of_use_enum.COMMERCIAL]: { main: '#22609B', light: '#22609B' },
  [class_of_use_enum.RESIDENTIAL]: { main: '#A7C0D7', light: '#A7C0D7' },
};

const taxBracketColors: Record<tax_bracket_enum, ShadedColor> = {
  [tax_bracket_enum._10_90]: createShadedColor(RED_ALT_WINE[500]),
  [tax_bracket_enum._20_80]: createShadedColor(RED_ALT_FIRE[500]),
  [tax_bracket_enum._30_70]: createShadedColor(ORANGE_ALT_CARMINE[500]),
  [tax_bracket_enum._40_60]: createShadedColor(ORANGE_ALT_ORANGE[500]),
  [tax_bracket_enum._50_50]: createShadedColor(YELLOW_ALT_SUN[500]),
  [tax_bracket_enum._60_40]: createShadedColor(YELLOW_ALT_OCRE[500]),
  [tax_bracket_enum._70_30]: createShadedColor(GREEN_ALT_OLIVE[500]),
  [tax_bracket_enum._80_20]: createShadedColor(GREEN_ALT_GRASS[500]),
  [tax_bracket_enum._90_10]: createShadedColor('#5FBA5A'),
  [tax_bracket_enum._100_0]: createShadedColor('#3A7B3A'),
};

const deviationFromDemandColors = {
  gt_30: RED_ALT_FIRE[500],
  pos_10_30: ORANGE_ALT_CARMINE[500],
  neg_10_10: GREEN_ALT_OLIVE[500],
  neg_30_neg_10: GREEN_ALT_GRASS[500],
  lt_neg_30: BLUE_ALT_OCEAN[500],
};

const energySourceTypeColors: Record<energy_source_type_enum, ShadedColor> = {
  [energy_source_type_enum.COAL]: createShadedColor('#000'),
  [energy_source_type_enum.ELECTRICITY_GREEN]: createShadedColor(GREEN_ALT_OLIVE[500]),
  [energy_source_type_enum.ELECTRICITY_MIX]: createShadedColor('#E1C300'),
  [energy_source_type_enum.FUEL_OIL]: createShadedColor('#525252'),
  [energy_source_type_enum.LIGNITE]: createShadedColor('#692C00'),
  [energy_source_type_enum.LPG]: createShadedColor('#C2DCDC'),
  [energy_source_type_enum.NATURAL_GAS]: createShadedColor('#22609B'),
  [energy_source_type_enum.SOLAR]: createShadedColor('#A68F00'),
  [energy_source_type_enum.WOOD]: createShadedColor(RED_ALT_WINE[500]),
  [energy_source_type_enum.WOODEN_PELLETS]: createShadedColor(RED_ALT_FIRE[500]),
  [energy_source_type_enum.UNSPECIFIED]: createShadedColor(GREY[400]),
  [energy_source_type_enum.BIO_GAS]: createShadedColor('#60F1C8'),
  [energy_source_type_enum.DISTRICT_HEATING_PLANTS_FOSSIL_COAL]: createShadedColor('#A7C0D7'),
  [energy_source_type_enum.DISTRICT_HEATING_PLANTS_FOSSIL_GAS]: createShadedColor('#FFBD98'),
  [energy_source_type_enum.DISTRICT_HEATING_PLANTS_RENEWABLE]: createShadedColor('#FFBD98'),
  [energy_source_type_enum.DISTRICT_HEATING_CHP_RENEWABLE]: createShadedColor('#FFBD98'),
  [energy_source_type_enum.DISTRICT_HEATING_CHP_FOSSIL_COAL]: createShadedColor('#FFBD98'),
  [energy_source_type_enum.DISTRICT_HEATING_CHP_FOSSIL_GAS]: createShadedColor('#FFBD98'),
};

const euTaxonomyColors: Record<eu_taxonomy_compliance_enum, ShadedColor> = {
  [eu_taxonomy_compliance_enum.COMPLIANT]: createShadedColor(GREEN_ALT_GRASS[500]),
  [eu_taxonomy_compliance_enum.MEETS_DNSH_CRITERIA]: createShadedColor(ORANGE_ALT_ORANGE[500], {
    light: YELLOW_ALT_SUN[300],
  }),
  [eu_taxonomy_compliance_enum.NON_COMPLIANT]: createShadedColor(RED_ALT_FIRE[500]),
  [eu_taxonomy_compliance_enum.UNKNOWN]: createShadedColor(GREY[400], { main: '#898F95' }),
};

const subsidyTypeColors: Record<subsidy_type_enum, ShadedColor> = {
  [subsidy_type_enum.GRANT]: createShadedColor('#1890FF'),
  [subsidy_type_enum.LOAN]: createShadedColor('#7B61FF'),
  [subsidy_type_enum.BOND]: createShadedColor('#B76E00'),
};

const consumptionColors = {
  [consumption_type_enum.ELECTRICITY]: createShadedColor(YELLOW_ALT_SUN[500]),
  [consumption_type_enum.HEATING]: createShadedColor(ORANGE_ALT_CARMINE[500]),
  [consumption_type_enum.WATER]: createShadedColor(BLUE_ALT_OCEAN[500]),
  [consumption_type_enum.WASTE]: createShadedColor(ORANGE_ALT_ORANGE[900]),
};

const excessEmissionCostColors = {
  cost: '#F48B84',
  value: '#A2D5A1',
};

const areaColors = {
  areas: [
    { bgColor: '#A7B0F1', textColor: '#283EE7' },
    { bgColor: '#EDC3F9', textColor: VIOLET[500] },
  ],
};

const customTargetColors = {
  marker: VIOLET[500],
  line: VIOLET[300],
};

const costByRenovationCategoryColors = {
  envelope: BLUE_ALT_NAVY[700],
  technology: BLUE_ALT_NAVY[500],
  energy_source: BLUE_ALT_NAVY[300],
  custom: BLUE_ALT_NAVY[100],
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
};

const CHART_COLORS = {
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4'],
};

// CONSTANTS
const PRIMARY_ENERGY = GREEN_ALT_GRASS[700];
const INVESTMENT = GREY[300];
const CO2_EMISSIONS = PRIMARY.main;
const FINAL_ENERGY = INFO.dark;
const TARGET_PATH = createShadedColor(BLUE_ALT_OCEAN[500]).light;
const STATUS_QUO = GREY[500];

const investmentCost = INVESTMENT;
const targetPath = TARGET_PATH;
const statusQuo = STATUS_QUO;
const strandingRange = ERROR.lighter;
const strandingPath = ERROR.main;

const scenarioComparisonColors = {
  scenarioOne: ORANGE_ALT_ORANGE[500],
  scenarioTwo: ORANGE_ALT_ORANGE[700],
  statusQuo: GREY[400],
};

const scenarioImpactGraphColors = {
  primaryEnergy: PRIMARY_ENERGY,
  yearlyCost: INVESTMENT,
  co2Emissions: CO2_EMISSIONS,
  finalEnergy: FINAL_ENERGY,
  targetPath: TARGET_PATH,
  statusQuo: STATUS_QUO,
};

const actionPlanImpactTrendsChartsColors: ActionPlanImpactTrendsChartsColors = {
  co2EmissionsPath: CO2_EMISSIONS,
  primaryEnergyPath: PRIMARY_ENERGY,
  finalEnergyPath: FINAL_ENERGY,
  investmentPath: investmentCost,
  targetPath: targetPath,
  statusQuoPath: statusQuo,
  strandingRange: ERROR.lighter,
  strandingPath: ERROR.main,
  strandingPathLight: ERROR.light,
  exclusionNote: GREY[500_16],
  textSecondary: SECONDARY.main,
  black: '#000',
  white: '#fff',
};

const heatingEnergySourcesDevelopment = {
  districtHeating: '#18629E',
  electricity: '#0C7FDD',
  gas: '#7CB7E7',
  oil: '#AFD7F8',
  other: GREY['400'],
};

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: { ...PRIMARY, contrastText: '#fff' },
  secondary: { ...SECONDARY, contrastText: '#fff' },
  info: { ...INFO, contrastText: '#fff' },
  success: { ...SUCCESS, contrastText: GREY[800] },
  warning: { ...WARNING, contrastText: GREY[800] },
  error: { ...ERROR, contrastText: '#fff' },
  violet: VIOLET,
  blue: BLUE,
  green: GREEN,
  grey: GREY,
  greenExtra: GREEN_EXTRA,
  yellow: YELLOW,
  red: RED,
  redAltFire: RED_ALT_FIRE,
  redAltWine: RED_ALT_WINE,
  blueAltNavy: BLUE_ALT_NAVY,
  blueAltOcean: BLUE_ALT_OCEAN,
  greenAltGrass: GREEN_ALT_GRASS,
  greenAltOlive: GREEN_ALT_OLIVE,
  yellowAltOcre: YELLOW_ALT_OCRE,
  yellowAltSun: YELLOW_ALT_SUN,
  orangeAltOrange: ORANGE_ALT_ORANGE,
  orangeAltCarmine: ORANGE_ALT_CARMINE,
  gradients: GRADIENTS,
  chart: CHART_COLORS,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
  link: '#1A73E8',
  investmentCost,
  targetPath,
  statusQuo,
  strandingRange,
  strandingPath,
  energyEfficiencyClassColors,
  energyEfficiencyClassColorsFR,
  energyEfficiencyClassColorsNL,
  co2EfficiencyClassColorsFR,
  climateRiskColors,
  classOfUseColors,
  taxBracketColors,
  energySourceTypeColors,
  euTaxonomyColors,
  subsidyTypeColors,
  consumptionColors,
  excessEmissionCostColors,
  scenarioImpactGraphColors,
  areaColors,
  scenarioComparisonColors,
  actionPlanImpactTrendsChartsColors,
  customTargetColors,
  costByRenovationCategoryColors,
  deviationFromDemandColors,
  heatingEnergySourcesDevelopment,
};

const palette = {
  light: {
    ...COMMON,
    mode: 'light',
    text: {
      primary: PRIMARY.main,
      secondary: GREY[600],
      disabled: GREY[500],
      info: INFO.main,
      success: SUCCESS.main,
      warning: WARNING.main,
      error: ERROR.main,
    },
    background: { paper: '#fff', default: '#fff', neutral: GREY[200] },
    action: { active: GREY[600], ...COMMON.action },
  },
  dark: {
    ...COMMON,
    mode: 'dark',
    text: { primary: '#fff', secondary: GREY[500], disabled: GREY[600] },
    background: { paper: GREY[800], default: GREY[900], neutral: GREY[500_16] },
    action: { active: GREY[500], ...COMMON.action },
  },
} as const;

export default palette;
